import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeftRight, Bot, CheckCircle2, ChevronDown, CircleDot, CloudUpload, Gauge, Loader2, RefreshCw, Search, Server, SlidersHorizontal, Swords, UserRound, UsersRound, XCircle } from 'lucide-react'
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { Badge, Button, Card, EmptyState, Field, Input, Modal, PageTitle, Textarea } from '../components/ui'
import { api } from '../lib/api'
import type { AdminAssetSummary, AdminCompetition, AdminScoreRunSummary, AdminUserOption } from '../lib/types'

type AssetsResponse = { items: AdminAssetSummary[]; total: number }
type CompetitionsResponse = { items: AdminCompetition[]; total: number }
type UsersResponse = { items: AdminUserOption[]; total: number }
type ScoreJob = { id: string; kind: string; status: string; attempts: number; score?: number | null; last_error?: string | null; attempt_log?: Array<Record<string, unknown>> | null; created_at: string; updated_at: string; completed_at?: string | null; claimed_at?: string | null; stale?: boolean }
type ScoreStatusResponse = { asset_id: string; submission_id: string; scoring_status: string; static_status?: string | null; dynamic_status?: string | null; final_score?: number | null; static_score?: number | null; dynamic_score?: number | null; static_error?: string | null; dynamic_error?: string | null; final_pass_score?: number | null; result_status?: string | null; result_frozen_at?: string | null; final_rank?: number | null; manual_result_status?: string | null; region_finalist_count?: number | null; region_eligible_count?: number | null; preview_rank?: number | null; preview_qualified?: boolean | null; jobs: ScoreJob[]; score_runs: AdminScoreRunSummary[]; process_steps: Array<Record<string, unknown>> }
type ScoreActionResponse = { score_run?: AdminScoreRunSummary | null; job?: ScoreJob | null; message: string }
type ScoreAction = 'score'

const sourceLabel = (source: string) => source === 'competition' ? '赛事投稿' : source === 'regular_upload' ? '常规上传' : source === 'console' ? '开发者控制台' : source === 'external' ? '外部地址' : source
const sourceTone = (source: string): 'amber' | 'blue' | 'neutral' => source === 'competition' ? 'amber' : source === 'regular_upload' ? 'blue' : 'neutral'
const statusLabel = (status?: string | null) => ({
  idle: '未开始',
  pending: '待处理',
  queued: '排队中',
  running: '执行中',
  provisioning: '准备环境',
  testing: '执行用例',
  completed: '已完成',
  skipped: '已跳过',
  failed: '失败',
  not_required: '无需评分',
  pending_parse: '等待解析',
  parsing: '解析中',
  pending_score: '待评分',
  static_running: '静态评分中',
  dynamic_running: '动态评分中',
  scored: '已评分',
  frozen: '已冻结',
  score_failed: '评分失败',
  parse_failed: '解析失败',
  static_failed: '静态评分失败',
  dynamic_failed: '动态评分失败',
  provision_failed: '环境准备失败',
}[status || ''] || status || '-')
const dimensionLabel = (key: string) => ({
  completion: '完成度',
  quality: '工程质量',
  usability: '可用性与部署',
  functionality: '功能完整性',
  engineering_quality: '工程质量',
  technical_depth: '技术深度',
  code_quality: '代码质量',
  task_completion: '任务完成度',
  response_efficiency: '响应效率',
  tool_health: '工具健康',
  reliability: '稳定性',
  performance: '性能表现',
  innovation: '创新性',
}[key] || key.replace(/_/g, ' '))
const scoreLabel = (asset: AdminAssetSummary) => asset.scoring_required ? `${statusLabel(asset.scoring_status)}${asset.latest_final_score == null ? '' : ` · ${asset.latest_final_score.toFixed(1)} 分`}` : '无需比赛评分'
const isPublished = (asset: AdminAssetSummary) => !!asset.result_frozen_at && asset.result_status !== 'pending' && asset.result_status !== 'evaluation_pending'
const syncIdentityOnly = (asset: AdminAssetSummary) => asset.mixer_sync_block_reason === '作者尚未关联微信 Union ID'

export function AdminAssetsPage() {
  const qc = useQueryClient()
  const [searchInput, setSearchInput] = useState('')
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState('')
  const [source, setSource] = useState('')
  const [scoringStatus, setScoringStatus] = useState('')
  const [showDrafts, setShowDrafts] = useState(false)
  const [competitionKey, setCompetitionKey] = useState('')
  const [action, setAction] = useState<{ asset: AdminAssetSummary; type: ScoreAction } | null>(null)
  const [syncDialog, setSyncDialog] = useState<AdminAssetSummary | null>(null)
  const [transferDialog, setTransferDialog] = useState<AdminAssetSummary | null>(null)
  const [syncing, setSyncing] = useState<string | null>(null)
  const [transferring, setTransferring] = useState<string | null>(null)
  const [publishing, setPublishing] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkPublishing, setBulkPublishing] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const competitions = useQuery({ queryKey: ['admin-asset-competitions'], queryFn: () => api<CompetitionsResponse>('/api/v1/admin/competitions') })
  const users = useQuery({ queryKey: ['admin-users-options'], queryFn: () => api<UsersResponse>('/api/v1/admin/users?limit=1000') })
  const [competitionId, regionId] = competitionKey ? competitionKey.split('::') : ['', '']
  const assets = useQuery({
    queryKey: ['admin-assets-management', query, kind, source, scoringStatus, competitionId, regionId, showDrafts],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '200' })
      if (query) params.set('q', query)
      if (kind) params.set('kind', kind)
      if (source) params.set('source', source)
      if (scoringStatus) params.set('scoring_status', scoringStatus)
      if (competitionId) params.set('competition_id', competitionId)
      if (regionId) params.set('region_id', regionId)
      if (showDrafts) params.set('include_drafts', 'true')
      return api<AssetsResponse>(`/api/v1/admin/assets?${params.toString()}`)
    },
  })

  const competitionOptions = useMemo(() => (competitions.data?.items || []).map(item => {
    const value = `${item.competition_id}::${item.region_id}`
    return { value, label: `${item.name} · ${item.competition_id}/${item.region_id}` }
  }), [competitions.data?.items])
  const items = assets.data?.items || []
  const competitionCount = items.filter(item => item.scoring_required).length
  const readyCount = items.filter(item => item.status === 'ready').length
  const selectableItems = items.filter(item => item.scoring_required && item.submission_id && item.submission_status !== 'draft')
  const selectableIds = selectableItems.map(item => item.submission_id as string)
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id))

  function toggleSelect(submissionId: string) {
    setSelected(previous => {
      const next = new Set(previous)
      if (next.has(submissionId)) next.delete(submissionId)
      else next.add(submissionId)
      return next
    })
  }

  function toggleSelectAll() {
    setSelected(previous => allSelected ? new Set() : new Set(selectableIds))
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault()
    setQuery(searchInput.trim())
  }

  async function syncAsset(asset: AdminAssetSummary, targetUserId: number | null) {
    setSyncing(asset.id)
    setMessage('')
    setError('')
    try {
      const body = targetUserId ? { target_user_id: targetUserId } : {}
      const result = await api<{ sync: AdminAssetSummary['latest_sync'] }>(`/api/v1/admin/assets/${asset.id}/sync`, { method: 'POST', body })
      setSyncDialog(null)
      setMessage(`同步成功：Mixer v${result.sync?.source_version ?? 1}`)
      await qc.invalidateQueries({ queryKey: ['admin-assets-management'] })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '同步失败')
    } finally {
      setSyncing(null)
    }
  }

  async function transferAsset(asset: AdminAssetSummary, targetUserId: number) {
    setTransferring(asset.id)
    setMessage('')
    setError('')
    try {
      await api(`/api/v1/admin/assets/${asset.id}/transfer`, { method: 'POST', body: { target_user_id: targetUserId } })
      setTransferDialog(null)
      setMessage(`「${asset.name}」已转移到目标账号`)
      await qc.invalidateQueries({ queryKey: ['admin-assets-management'] })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '转移失败')
    } finally {
      setTransferring(null)
    }
  }

  async function publishResults() {
    if (!competitionId || !regionId) return
    if (!window.confirm('将立即公布该赛区的晋级结果，选手只会看到晋级/未晋级，不会看到分数或排名。确认发布？')) return
    setPublishing(true)
    setMessage('')
    setError('')
    try {
      const result = await api<{ changed: number; status: string }>(`/api/v1/admin/competitions/${competitionId}/${regionId}/publish`, { method: 'POST' })
      setMessage(result.changed > 0 ? `已发布，${result.changed} 份投稿的结果已更新` : '该赛区已处于发布状态，无新变更')
      await qc.invalidateQueries({ queryKey: ['admin-assets-management'] })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '发布失败')
    } finally {
      setPublishing(false)
    }
  }

  async function publishSelected() {
    const submissionIds = [...selected]
    if (!submissionIds.length) return
    if (!window.confirm(`将立即公布所选 ${submissionIds.length} 份投稿的晋级结果，选手只会看到晋级/未晋级，不会看到分数或排名。确认发布？`)) return
    setBulkPublishing(true)
    setMessage('')
    setError('')
    try {
      const result = await api<{ changed: number; found: number; status: string }>('/api/v1/admin/competitions/publish-submissions', {
        method: 'POST',
        body: { submission_ids: submissionIds, reason: '管理员从资产管理按投稿发布', confirm: true },
      })
      setMessage(result.changed > 0 ? `已发布，${result.changed} 份投稿的结果已更新` : '所选投稿已处于发布状态，无新变更')
      setSelected(new Set())
      await qc.invalidateQueries({ queryKey: ['admin-assets-management'] })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '发布失败')
    } finally {
      setBulkPublishing(false)
    }
  }

  return <div className="workspace-page data-center-page">
    <PageTitle eyebrow="ADMIN · ASSETS" title="资产管理" description="查看全平台 Agent 与 MCP 资产，区分常规上传和赛事投稿，并对赛事资产执行评分、覆盖、转移归属和同步到 Mixer。" actions={<Button variant="secondary" onClick={() => assets.refetch()}><RefreshCw size={15}/>刷新</Button>}/>
    {message && <div className="form-success page-message">{message}</div>}{error && <div className="form-error page-message">{error}</div>}
    <div className="metrics-row"><Metric icon={<Server/>} label="当前结果资产" value={assets.data?.total || 0}/><Metric icon={<Gauge/>} label="赛事评分资产" value={competitionCount}/><Metric icon={<Bot/>} label="Ready" value={readyCount}/></div>
    <Card className="data-center-filters">
      <form className="data-center-search" onSubmit={submitSearch}>
        <div className="search-box"><Search size={16}/><Input value={searchInput} onChange={event => setSearchInput(event.target.value)} placeholder="搜索资产、作者、比赛、说明"/></div>
        <Button type="submit" variant="secondary">搜索</Button>
        {query && <Button type="button" variant="ghost" onClick={() => { setSearchInput(''); setQuery('') }}>清除</Button>}
      </form>
      <div className="kind-switch" role="group" aria-label="资产类型">
        <button className={kind === '' ? 'active' : ''} onClick={() => setKind('')}>全部</button>
        <button className={kind === 'agent' ? 'active' : ''} onClick={() => setKind('agent')}>Agent</button>
        <button className={kind === 'mcp_server' ? 'active' : ''} onClick={() => setKind('mcp_server')}>MCP</button>
      </div>
      <div className="asset-filter-row">
        <label><Swords size={14}/><select className="input" value={competitionKey} onChange={event => setCompetitionKey(event.target.value)} aria-label="按比赛筛选"><option value="">{competitions.isLoading ? '正在读取比赛…' : '全部比赛'}</option>{competitionOptions.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
        {competitionKey && <Button variant="secondary" loading={publishing} onClick={() => void publishResults()}><CheckCircle2 size={14}/>发布结果</Button>}
      </div>
      <div className="score-run-filters">
        {[['', '全部来源'], ['competition', '赛事投稿'], ['regular', '常规资产']].map(([value, label]) => <button type="button" key={value || 'source-all'} className={source === value ? 'active' : ''} onClick={() => setSource(value)}>{label}</button>)}
        {[['', '全部评分'], ['not_required', '无需评分'], ['pending_score', '待评分'], ['static_running', '静态中'], ['dynamic_running', '动态中'], ['scored', '已评分'], ['parse_failed', '解析失败'], ['static_failed', '静态失败'], ['dynamic_failed', '动态失败'], ['provision_failed', '环境失败'], ['score_failed', '失败']].map(([value, label]) => <button type="button" key={value || 'score-all'} className={scoringStatus === value ? 'active' : ''} onClick={() => setScoringStatus(value)}>{label}</button>)}
        <button type="button" className={showDrafts ? 'active' : ''} onClick={() => setShowDrafts(value => !value)} title="草稿投稿与无投稿的孤儿赛事资产默认隐藏，可开启查看以审计或清理">{showDrafts ? '显示草稿' : '隐藏草稿'}</button>
      </div>
    </Card>
    {assets.isLoading ? <div className="page-loader">正在读取资产…</div> : !items.length ? <EmptyState title="没有匹配的资产" description="调整搜索、比赛或类型筛选后重试。"/> : <>
      {selectableItems.length > 0 && <div className="asset-bulk-toolbar">
        <label className="asset-bulk-select"><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} aria-label="全选可发布投稿"/><span>全选</span></label>
        <Button variant="secondary" loading={bulkPublishing} disabled={selected.size === 0} onClick={() => void publishSelected()}><CheckCircle2 size={14}/>发布选中（{selected.size}）</Button>
      </div>}
      <div className="asset-admin-list">{items.map(item => <AssetRow key={item.id} item={item} syncing={syncing === item.id} transferring={transferring === item.id} selected={!!item.submission_id && selected.has(item.submission_id)} toggle={() => item.submission_id && toggleSelect(item.submission_id)} open={setAction} sync={() => setSyncDialog(item)} transfer={() => setTransferDialog(item)}/>)}</div>
    </>}
    <ScoreWorkModal action={action} close={() => setAction(null)} done={text => { setMessage(text); setError(''); void qc.invalidateQueries({ queryKey: ['admin-assets-management'] }) }} fail={text => { setError(text); setMessage('') }}/>
    <SyncAssetModal asset={syncDialog} users={users.data?.items || []} busy={syncing === syncDialog?.id} close={() => setSyncDialog(null)} submit={syncAsset}/>
    <TransferAssetModal asset={transferDialog} busy={transferring === transferDialog?.id} close={() => setTransferDialog(null)} submit={transferAsset}/>
  </div>
}

function AssetRow({ item, syncing, transferring, selected, toggle, open, sync, transfer }: { item: AdminAssetSummary; syncing: boolean; transferring: boolean; selected: boolean; toggle: () => void; open: (value: { asset: AdminAssetSummary; type: ScoreAction }) => void; sync: () => void; transfer: () => void }) {
  const selectable = item.scoring_required && !!item.submission_id && item.submission_status !== 'draft'
  return <Card className="asset-admin-card">
    <div className="asset-admin-main">
      {selectable ? <label className="asset-row-select"><input type="checkbox" checked={selected} onChange={toggle} aria-label={`选择 ${item.name}`}/></label> : <span className="asset-row-select-spacer"/>}
      <div className="data-primary"><span className="data-kind-icon">{item.kind === 'agent' ? <Bot/> : <Server/>}</span><div><strong title={item.name}>{item.name}</strong><small title={item.description || item.mcp_url || ''}>{item.description || item.mcp_url || '暂无说明'}</small></div></div>
      <div className="data-author"><UserRound size={14}/><div><strong title={item.author_display_name || item.author_username}>{item.author_display_name || item.author_username}</strong><small title={`@${item.author_username}`}>@{item.author_username}</small></div></div>
      <div className="asset-competition-cell">{item.competition_id ? <><Badge tone="amber">比赛</Badge><small title={`${item.competition_name || item.competition_id} · ${item.competition_id}/${item.region_id}`}>{item.competition_name || item.competition_id}<span>{item.competition_id}/{item.region_id}</span></small></> : <span className="muted-dash">-</span>}</div>
      <div className="asset-meta-stack"><Badge tone={sourceTone(item.source_type)}>{sourceLabel(item.source_type)}</Badge>{item.submission_status === 'draft' && <Badge tone="neutral">草稿</Badge>}{item.source_type === 'competition' && !item.submission_id && <Badge tone="neutral">孤儿资产</Badge>}<Badge tone={item.scoring_required ? scoreTone(item.scoring_status) : 'neutral'}>{scoreLabel(item)}</Badge>{isPublished(item) && <Badge tone="green">已发布</Badge>}</div>
      <div className="asset-definition-cell"><strong>v{item.definition_version || 1}</strong><small>{item.latest_sync ? `Mixer v${item.latest_sync.source_version}` : `${item.tool_count ?? 0} tools`}</small></div>
    </div>
    <div className="asset-admin-actions">
      {item.scoring_required ? <Button onClick={() => open({ asset: item, type: 'score' })}><Gauge size={14}/>评分管理</Button> : <Badge tone="neutral">标准资产</Badge>}
      <Button variant="secondary" loading={transferring} onClick={transfer} title="将该资产转移到平台下的其他账号名下"><ArrowLeftRight size={14}/>转移</Button>
      <Button variant="secondary" loading={syncing} disabled={!item.mixer_sync_eligible && !syncIdentityOnly(item)} title={item.mixer_sync_block_reason || '同步到 Mixer（微信小程序平台）'} onClick={sync}><CloudUpload size={14}/>{item.latest_sync ? '再同步到 Mixer' : '同步到 Mixer'}</Button>
    </div>
  </Card>
}

function ScoreWorkModal({ action, close, done, fail }: { action: { asset: AdminAssetSummary; type: ScoreAction } | null; close: () => void; done: (text: string) => void; fail: (text: string) => void }) {
  const qc = useQueryClient()
  const [reason, setReason] = useState('管理员从资产管理发起重新评分')
  const [overrideComponent, setOverrideComponent] = useState('')
  const [overrideScore, setOverrideScore] = useState('')
  const [manualReason, setManualReason] = useState('')
  const [busyAction, setBusyAction] = useState<'static' | 'dynamic' | 'both' | 'override' | 'publish' | 'verdict' | null>(null)
  const isOpen = !!action
  const status = useQuery({
    queryKey: ['admin-asset-score-status', action?.asset.id],
    enabled: isOpen && !!action?.asset.submission_id && action?.asset.scoring_required,
    queryFn: () => api<ScoreStatusResponse>(`/api/v1/admin/assets/${action?.asset.id}/score/status`),
    refetchInterval: query => hasActiveScoreJob(query.state.data) ? 2500 : false,
  })
  useEffect(() => {
    setReason('管理员从资产管理发起重新评分')
    setOverrideComponent('')
    setOverrideScore('')
    setManualReason('')
    setBusyAction(null)
  }, [action?.asset.id, action?.type])

  useEffect(() => {
    if (!isOpen || hasActiveScoreJob(status.data)) return
    void qc.invalidateQueries({ queryKey: ['admin-assets-management'] })
  }, [isOpen, status.data, qc])

  async function trigger(kind: 'static' | 'dynamic') {
    if (!action) return
    setBusyAction(kind)
    try {
      const response = await api<ScoreActionResponse>(`/api/v1/admin/assets/${action.asset.id}/score/${kind}`, { method: 'POST', body: { reason, confirm: true } })
      done(response.message)
      await status.refetch()
    } catch (error) {
      fail(error instanceof Error ? error.message : '操作失败')
    } finally {
      setBusyAction(null)
    }
  }

  async function triggerBoth() {
    if (!action) return
    setBusyAction('both')
    try {
      const results: string[] = []
      for (const [kind, label] of [['static', '静态'], ['dynamic', '动态']] as const) {
        try {
          const response = await api<ScoreActionResponse>(`/api/v1/admin/assets/${action.asset.id}/score/${kind}`, { method: 'POST', body: { reason, confirm: true } })
          results.push(`${label}：${response.message}`)
        } catch (error) {
          results.push(`${label}：${error instanceof Error ? error.message : '触发失败'}`)
        }
      }
      done(results.join('；'))
      await status.refetch()
    } finally {
      setBusyAction(null)
    }
  }

  async function setVerdict(verdict: 'qualified' | 'not_qualified' | null) {
    if (!action || !action.asset.submission_id || !action.asset.competition_id || !action.asset.region_id) return
    setBusyAction('verdict')
    try {
      await api(`/api/v1/admin/competitions/${encodeURIComponent(action.asset.competition_id)}/${encodeURIComponent(action.asset.region_id)}/submissions/${action.asset.submission_id}/verdict`, {
        method: 'POST',
        body: { verdict, reason: manualReason, confirm: true },
      })
      done(verdict === 'qualified' ? '已手动判定为通过' : verdict === 'not_qualified' ? '已手动判定为未通过' : '已恢复自动排名判定')
      await status.refetch()
      setManualReason('')
    } catch (error) {
      fail(error instanceof Error ? error.message : '设置判定失败')
    } finally {
      setBusyAction(null)
    }
  }

  async function submitOverride(event: FormEvent) {
    event.preventDefault()
    if (!action || !overrideComponent || overrideScore === '') return
    const sep = overrideComponent.indexOf('::')
    if (sep <= 0) return
    const scoreKind = overrideComponent.slice(0, sep)
    const componentKey = overrideComponent.slice(sep + 2)
    setBusyAction('override')
    try {
      const response = await api<ScoreActionResponse>(`/api/v1/admin/assets/${action.asset.id}/score/override`, {
        method: 'POST',
        body: { score_kind: scoreKind, component_key: componentKey, score: Number(overrideScore), reason, confirm: true },
      })
      done(response.message)
      await status.refetch()
      setOverrideScore('')
    } catch (error) {
      fail(error instanceof Error ? error.message : '覆盖分项失败')
    } finally {
      setBusyAction(null)
    }
  }

  async function publishSingle() {
    if (!action?.asset.submission_id) return
    const published = !!status.data?.result_frozen_at
    if (!window.confirm(published ? '该投稿结果已发布，重新发布将刷新晋级/未晋级判定。确认继续？' : '将立即公布该投稿结果，选手将看到晋级/未晋级。确认发布？')) return
    setBusyAction('publish')
    try {
      const response = await api<{ changed: number; status: string }>('/api/v1/admin/competitions/publish-submissions', {
        method: 'POST',
        body: { submission_ids: [action.asset.submission_id], reason, confirm: true },
      })
      done(response.changed > 0 ? '结果已发布，选手可见晋级/未晋级' : '该投稿已处于发布状态，无新变更')
      await status.refetch()
      await qc.invalidateQueries({ queryKey: ['admin-assets-management'] })
    } catch (error) {
      fail(error instanceof Error ? error.message : '发布失败')
    } finally {
      setBusyAction(null)
    }
  }

  const overrideOptions = scoreComponentOptions(status.data)
  useEffect(() => {
    if (!overrideOptions.some(item => `${item.kind}::${item.key}` === overrideComponent)) {
      setOverrideComponent(overrideOptions[0] ? `${overrideOptions[0].kind}::${overrideOptions[0].key}` : '')
    }
  }, [overrideOptions, overrideComponent])

  return <Modal open={isOpen} onOpenChange={value => !value && close()} title="评分管理" description={action ? `资产：${action.asset.name}` : undefined} className="score-live-dialog">
    {action && <div className="score-modal-content">
      {status.data ? <ScoreSnapshot status={status.data}/> : <ScoreStatusPlaceholder loading={status.isLoading} error={status.error instanceof Error ? status.error.message : ''}/>}
      <section className="score-trigger-panel">
        <div><strong>重新触发评分</strong><small>评分会在后台继续执行，关闭窗口后再次打开即可续接状态。</small></div>
        <Field label="触发原因" required><Textarea value={reason} onChange={event => setReason(event.target.value)} required minLength={2} placeholder="记录本次重新评分的依据。"/></Field>
        <div className="modal-actions-inline score-trigger-actions">
          <Button type="button" loading={busyAction === 'static'} disabled={hasActiveKind(status.data, 'static')} onClick={() => void trigger('static')}>{hasActiveKind(status.data, 'static') ? '静态评分处理中' : '触发静态评分'}</Button>
          <Button type="button" variant="secondary" loading={busyAction === 'dynamic'} disabled={hasActiveKind(status.data, 'dynamic')} onClick={() => void trigger('dynamic')}>{hasActiveKind(status.data, 'dynamic') ? '动态评分处理中' : '触发动态评分'}</Button>
          <Button type="button" variant="secondary" loading={busyAction === 'both'} disabled={hasActiveKind(status.data, 'static') || hasActiveKind(status.data, 'dynamic')} onClick={() => void triggerBoth()}>同时触发（静态+动态）</Button>
        </div>
      </section>
      {action.asset.source_type === 'competition' && <section className="score-verdict-panel">
        <div><strong>手动判定</strong><small>绑定当前参赛记录 ID，覆盖自动排名和“最新投稿”去重规则。已发布投稿修改后立即生效；未发布投稿还需点击发布结果。</small></div>
        <Field label="判定原因" required><Textarea value={manualReason} onChange={event => setManualReason(event.target.value)} required minLength={2} placeholder="记录手动判定的依据。"/></Field>
        <div className="modal-actions-inline">
          <Button type="button" loading={busyAction === 'verdict'} disabled={!status.data || !manualReason.trim()} onClick={() => void setVerdict('qualified')}><CheckCircle2 size={14}/>设为通过</Button>
          <Button type="button" variant="secondary" loading={busyAction === 'verdict'} disabled={!status.data || !manualReason.trim()} onClick={() => void setVerdict('not_qualified')}><XCircle size={14}/>设为未通过</Button>
          <Button type="button" variant="ghost" loading={busyAction === 'verdict'} disabled={!status.data?.manual_result_status} onClick={() => void setVerdict(null)}>恢复自动判定</Button>
        </div>
      </section>}
      <details className="score-adjust-panel">
        <summary><SlidersHorizontal size={15}/><span>调整评分分项</span><ChevronDown size={15}/></summary>
        <form className="score-adjust-form" onSubmit={submitOverride}>
          <Field label="评分分项" required hint="静态与动态的分项合并展示；选哪个就改哪个，自动按分项归属调用对应评分。"><select className="input" value={overrideComponent} onChange={event => setOverrideComponent(event.target.value)} required><option value="" disabled>请选择分项</option>{overrideOptions.map(item => <option key={`${item.kind}::${item.key}`} value={`${item.kind}::${item.key}`}>{scoreKindLabel(item.kind)} · {item.label}（当前 {formatScore(item.score)} 分）</option>)}</select></Field>
          <Field label="覆盖分数" required hint="只覆盖当前分项；静态/动态总分及最终分会自动重新计算。"><Input type="number" step="0.1" min="0" max="100" value={overrideScore} onChange={event => setOverrideScore(event.target.value)} required/></Field>
          <Field label="覆盖原因" required><Textarea value={reason} onChange={event => setReason(event.target.value)} required minLength={2}/></Field>
          <Button type="submit" loading={busyAction === 'override'} disabled={!overrideComponent || overrideScore === ''}>保存分项覆盖</Button>
        </form>
      </details>
      {status.data && <ScoreHistory status={status.data}/>}
      <div className="modal-actions-inline">
        {action.asset.submission_id && <Button type="button" variant="secondary" loading={busyAction === 'publish'} disabled={status.data?.result_status === 'superseded'} onClick={() => void publishSingle()}><CheckCircle2 size={14}/>{status.data?.result_status === 'superseded' ? '历史投稿不发布' : status.data?.result_frozen_at ? `重新发布${status.data.final_rank ? ` · 排名 ${status.data.final_rank}` : ''}` : '发布结果'}</Button>}
        <Button type="button" variant="secondary" onClick={close}>关闭</Button>
      </div>
    </div>}
  </Modal>
}

function ScoreStatusPlaceholder({ loading, error }: { loading: boolean; error: string }) {
  if (loading) return <div className="score-live-flow"><p className="score-empty">正在读取评分状态…</p></div>
  if (error) return <div className="form-error">{error}</div>
  return <div className="score-live-flow"><p className="score-empty">暂无评分状态。</p></div>
}

function ScoreSnapshot({ status }: { status: ScoreStatusResponse }) {
  // Latest generated scores: the three summary cards plus the run log that
  // produced them. Sits at the top of the dialog so the score is the first
  // thing an admin sees.
  return <div className="score-workspace">
    <div className="score-live-summary">
      <ScoreSummaryCard label="静态" score={status.static_score} status={status.static_status} run={latestRun(status, 'static')}/>
      <ScoreSummaryCard label="动态" score={status.dynamic_score} status={status.dynamic_status} run={latestRun(status, 'dynamic')} fallbackReason={status.dynamic_error}/>
      <div><small>最终</small><strong>{status.final_score != null ? formatScore(status.final_score) : '—'}</strong><span>{status.final_score != null ? `${statusLabel(status.scoring_status)} · /105` : '评测中'}</span></div>
      <VerdictSummary status={status}/>
    </div>
    <details className="score-process-panel">
      <summary><span><CircleDot size={15}/></span><strong>评分日志</strong><small>{status.process_steps.length} 条记录，默认收起</small><ChevronDown size={15}/></summary>
      <div className="score-live-flow" aria-live="polite">
        {status.process_steps.map((step, index) => <ScoreProcessLine key={String(step.key || index)} step={step}/>) }
      </div>
    </details>
  </div>
}

function ScoreHistory({ status }: { status: ScoreStatusResponse }) {
  const runs = status.score_runs
    .filter(run => run.score_kind === 'dynamic')
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
  return <details className="score-history-panel">
    <summary><span><RefreshCw size={15}/></span><strong>评分历史</strong><small>{runs.length} 条记录</small><ChevronDown size={15}/></summary>
    <div className="score-run-detail-list">
      {runs.map(run => <ScoreRunMini key={run.id} run={run}/>)}
    </div>
  </details>
}

function ScoreSummaryCard({ label, score, status, run, fallbackReason }: { label: string; score?: number | null; status?: string | null; run?: AdminScoreRunSummary; fallbackReason?: string | null }) {
  const components = run ? scoreDimensionsForRun(run) : []
  const displayScore = (status === 'completed' || status === 'skipped') ? formatScore(score) : '—'
  const reason = fallbackReason || (run ? summarizeRun(run).reason : '')
  return <div className="score-summary-card"><small>{label}</small><strong>{displayScore}</strong><span>{statusLabel(status)}</span>{(status === 'skipped' || status === 'failed') && reason && <p className="score-summary-reason">{reason}</p>}{components.length > 0 && <div className="score-hover-detail" role="tooltip"><b>{label}评分组成</b>{components.map(item => <div key={item.key}><span>{item.label}</span><em>{formatScore(item.score)} × {Math.round((item.weight || 0) * 100)}% = {formatScore(item.contribution)}</em></div>)}<footer>加权总分：{formatScore(components.reduce((sum, item) => sum + (item.contribution || 0), 0))}</footer></div>}</div>
}

// 过/不过始终按赛区规则判定：综合分排名前 region_finalist_count 名通过。
// 已冻结时展示落库的权威结论；未冻结时展示与发布同口径的赛区排名预览；
// 管理员手动判定时优先展示手动结论并标注。
function VerdictSummary({ status }: { status: ScoreStatusResponse }) {
  const frozenVerdict = !!status.result_frozen_at && (status.result_status === 'qualified' || status.result_status === 'not_qualified')
  const manual = status.manual_result_status === 'qualified' || status.manual_result_status === 'not_qualified'
  const manualTag = manual ? ' · 手动判定' : ''
  if (status.result_status === 'superseded') {
    return <div><small>结果</small><strong>已被最新投稿替代</strong><span>保留评分，不参与排名且不对外发布结果</span></div>
  }
  if (status.final_score == null) {
    return <div><small>结果</small><strong>—</strong><span>静态+动态完成后评定</span></div>
  }
  if (frozenVerdict) {
    return <div><small>结果</small><strong>{status.result_status === 'qualified' ? '通过' : '未通过'}</strong><span>已发布{status.final_rank != null ? ` · 赛区排名 #${status.final_rank}` : ''} · 入围 {status.region_finalist_count ?? '—'}{manualTag}</span></div>
  }
  if (manual) {
    return <div><small>结果</small><strong>{status.manual_result_status === 'qualified' ? '通过' : '未通过'}</strong><span>手动判定 · 入围 {status.region_finalist_count ?? '—'}</span></div>
  }
  if (status.preview_rank != null) {
    return <div><small>结果（预览）</small><strong>{status.preview_qualified ? '通过' : '未通过'}</strong><span>赛区排名 #{status.preview_rank}（共 {status.region_eligible_count ?? '—'} 人）· 入围 {status.region_finalist_count ?? '—'}</span></div>
  }
  return <div><small>结果</small><strong>待定</strong><span>完成评测后按赛区排名前 {status.region_finalist_count ?? 'N'} 判定</span></div>
}

function ScoreProcessLine({ step }: { step: Record<string, unknown> }) {
  const state = stepStatus(step)
  return <details className={`score-process-line is-${state}`}>
    <summary><span>{state === 'completed' ? <CheckCircle2/> : state === 'running' ? <Loader2/> : <CircleDot/>}</span><strong>{String(step.label || '')}</strong><small>{statusLabel(String(step.job_status || state))}</small><ChevronDown size={14}/></summary>
    <div><p>{String(step.message || '暂无过程说明。')}</p>{Boolean(step.job_id) && <small>任务 {String(step.job_id).slice(0, 8)} · {statusLabel(String(step.job_status || ''))} · 第 {Number(step.attempts || 0)} 次</small>}</div>
  </details>
}

function ScoreRunMini({ run }: { run: AdminScoreRunSummary }) {
  const details = summarizeRun(run)
  return <details className="score-run-mini">
    <summary><span>{scoreKindLabel(run.score_kind)}</span><strong>{formatScore(run.score_100 ?? run.effective_score)}</strong><small>{new Date(run.created_at).toLocaleString('zh-CN', { hour12: false })} · {run.is_active ? '当前记录' : '历史记录'} · {run.adjustment_reason || '无触发原因'}</small><ChevronDown size={14}/></summary>
    <div className="score-run-mini-body">
      <div><small>输入</small><p>{details.input}</p></div>
      <div><small>结果</small><p>{details.result}</p></div>
      <div><small>原因/过程</small><p>{details.reason}</p></div>
      <pre>{JSON.stringify(run.result_json || {}, null, 2)}</pre>
    </div>
  </details>
}

function summarizeRun(run: AdminScoreRunSummary) {
  const json = run.result_json || {}
  const input = (json.input && typeof json.input === 'object') ? json.input as Record<string, unknown> : {}
  const result = (json.result && typeof json.result === 'object') ? json.result as Record<string, unknown> : json
  const dimensions = readScoreDimensions(json)
  const rounds = Array.isArray(json.rounds) ? json.rounds : Array.isArray(json.cases) ? json.cases : []
  return {
    input: Object.keys(input).length ? JSON.stringify(input) : run.scoring_policy_id || run.source_job_id || '评分器根据提交包、解析结果和赛事策略生成输入。',
    result: dimensions.length ? dimensions.map(item => `${dimensionLabel(item.name)}：${formatScore(item.score)}`).join('；') : rounds.length ? `动态用例 ${rounds.length} 条，得分 ${formatScore(run.score_100)}` : `得分 ${formatScore(run.score_100 ?? run.effective_score)}`,
    reason: run.adjustment_reason || String((json as Record<string, unknown>).summary || (json as Record<string, unknown>).reason || (json as Record<string, unknown>).description || '评分过程已记录在原始 JSON 中。'),
  }
}

function SyncAssetModal({ asset, users, busy, close, submit }: { asset: AdminAssetSummary | null; users: AdminUserOption[]; busy: boolean; close: () => void; submit: (asset: AdminAssetSummary, targetUserId: number | null) => Promise<void> }) {
  const [target, setTarget] = useState('')
  useEffect(() => {
    setTarget(asset?.author_id ? String(asset.author_id) : '')
  }, [asset?.id, asset?.author_id])
  if (!asset) return null
  const ownerName = asset.author_display_name || asset.author_username
  return <Modal open={!!asset} onOpenChange={value => !value && close()} title="同步到 Mixer" description={`资产：${asset.name}`}>
    <form className="stack" onSubmit={event => { event.preventDefault(); void submit(asset, target ? Number(target) : null) }}>
      <div className="sync-target-note"><UsersRound/><div><strong>默认同步给创建者</strong><p>{ownerName ? `${ownerName}（@${asset.author_username}）` : '该资产缺少明确创建者，请手动选择成员。'}</p></div></div>
      <Field label="同步成员" required hint="可改为任意已绑定 Mixer 身份的成员。"><select className="input" value={target} onChange={event => setTarget(event.target.value)} required><option value="" disabled>请选择成员</option>{users.map(user => <option key={user.id} value={user.id}>{user.wechat_nickname || user.username}（@{user.username}）</option>)}</select></Field>
      {asset.mixer_sync_block_reason && <div className="modal-tip"><CloudUpload/><span>{asset.mixer_sync_block_reason}</span></div>}
      <Button type="submit" loading={busy}><CloudUpload size={15}/>确认同步</Button>
    </form>
  </Modal>
}

function TransferAssetModal({ asset, busy, close, submit }: { asset: AdminAssetSummary | null; busy: boolean; close: () => void; submit: (asset: AdminAssetSummary, targetUserId: number) => Promise<void> }) {
  const [keyword, setKeyword] = useState('')
  const [debounced, setDebounced] = useState('')
  const [target, setTarget] = useState<AdminUserOption | null>(null)
  useEffect(() => {
    setKeyword(''); setDebounced(''); setTarget(null)
  }, [asset?.id])
  // Debounce the search keyword so we don't fire a request on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(keyword.trim()), 300)
    return () => clearTimeout(handle)
  }, [keyword])
  const results = useQuery({
    queryKey: ['admin-users-search', debounced],
    queryFn: () => api<UsersResponse>(`/api/v1/admin/users?q=${encodeURIComponent(debounced)}&limit=50`),
    enabled: !!asset && debounced.length >= 1,
  })
  if (!asset) return null
  const ownerName = asset.author_display_name || asset.author_username
  // Exclude the current owner — transferring to self is rejected by the backend.
  const candidates = (results.data?.items || []).filter(user => user.id !== asset.author_id)
  return <Modal open={!!asset} onOpenChange={value => !value && close()} title="转移资产归属" description={`资产：${asset.name}`}>
    <form className="stack" onSubmit={event => { event.preventDefault(); if (target) void submit(asset, target.id) }}>
      <div className="sync-target-note"><ArrowLeftRight/><div><strong>当前归属：{ownerName}（@{asset.author_username}）</strong><p>转移后，该资产及其上传的源码包、评分记录和参赛信息将归属目标账号，原账号不再可见。</p></div></div>
      {target ? <div className="transfer-picked">
        <div><UserRound size={15}/><strong>{target.wechat_nickname || target.username}</strong><small>@{target.username}{target.email ? ` · ${target.email}` : ''}</small></div>
        <Button type="button" variant="ghost" onClick={() => setTarget(null)}>重选</Button>
      </div> : <>
        <Field label="搜索目标账号" required hint="输入用户名、昵称或邮箱关键词进行搜索。"><div className="search-box"><Search size={16}/><Input value={keyword} onChange={event => setKeyword(event.target.value)} placeholder="搜索用户名 / 昵称 / 邮箱" autoFocus/></div></Field>
        {debounced && <div className="transfer-results">
          {results.isLoading && <small className="asset-detail-empty">正在搜索…</small>}
          {!results.isLoading && !candidates.length && <small className="asset-detail-empty">没有匹配的账号，换个关键词试试。</small>}
          {candidates.map(user => <button type="button" key={user.id} className="transfer-result-row" onClick={() => setTarget(user)}>
            <UserRound size={16}/>
            <div className="transfer-result-meta"><strong>{user.wechat_nickname || user.username}</strong><small>@{user.username}{user.email ? ` · ${user.email}` : ''}</small></div>
          </button>)}
        </div>}
      </>}
      <Button type="submit" loading={busy} disabled={!target}><ArrowLeftRight size={15}/>确认转移</Button>
    </form>
  </Modal>
}

// A job still marked "running" with an expired lease (worker crash / gateway
// restart) is stale: it is not actually executing, so it must not block the
// trigger buttons. The backend reaps stale jobs and re-queues them.
const jobActive = (job: ScoreJob) => job.status === 'queued' || (job.status === 'running' && !job.stale)

function hasActiveScoreJob(status?: ScoreStatusResponse) {
  return !!status?.jobs.some(jobActive)
}

function hasActiveKind(status: ScoreStatusResponse | undefined, kind: 'static' | 'dynamic') {
  const jobKind = `${kind}_score`
  return !!status?.jobs.some(job => job.kind === jobKind && jobActive(job))
}

function stepStatus(step: Record<string, unknown>) {
  return String(step.status || 'pending')
}

function readScoreDimensions(result: Record<string, unknown> | null | undefined): Array<{ name: string; score: number | null }> {
  const dimensions = result?.dimensions
  if (!dimensions || typeof dimensions !== 'object') return []
  return Object.entries(dimensions as Record<string, unknown>).map(([name, value]) => {
    const row = value && typeof value === 'object' ? value as Record<string, unknown> : {}
    const score = typeof row.score === 'number' ? row.score : typeof row.score_100 === 'number' ? row.score_100 : null
    return { name, score }
  })
}

function latestRun(status: ScoreStatusResponse, kind: 'static' | 'dynamic') {
  return status.score_runs.find(run => run.score_kind === kind && run.is_active) || status.score_runs.find(run => run.score_kind === kind)
}

function scoreDimensionsForRun(run: AdminScoreRunSummary) {
  const result = run.result_json || {}
  const source = run.score_kind === 'static' && result.dimensions && typeof result.dimensions === 'object' ? result.dimensions as Record<string, unknown> : result
  // Static rubric dimensions are completion/quality/usability; dynamic ones are
  // task_completion/response_efficiency/tool_health — mirror the backend's
  // override component weights exactly so the dropdown is never empty.
  const defaultWeights = run.score_kind === 'static'
    ? { completion: 0.45, quality: 0.35, usability: 0.2 }
    : { task_completion: 0.45, response_efficiency: 0.3, tool_health: 0.25 }
  const weights: Record<string, unknown> = result.weights && typeof result.weights === 'object' ? result.weights as Record<string, unknown> : defaultWeights
  const keys = run.score_kind === 'static' ? ['completion', 'quality', 'usability'] : ['task_completion', 'response_efficiency', 'tool_health']
  return keys.map(key => {
    const row = source[key] && typeof source[key] === 'object' ? source[key] as Record<string, unknown> : {}
    const score = typeof row.score === 'number' ? row.score : typeof row.score_100 === 'number' ? row.score_100 : null
    const weightValue = weights[key]
    const weight = typeof weightValue === 'number' ? weightValue : 0
    return { kind: run.score_kind as 'static' | 'dynamic', key, label: dimensionLabel(key), score, weight, contribution: score == null ? null : score * weight }
  }).filter(item => item.score != null)
}

// Unified override list across static and dynamic kinds. The backend's
// `score_kind` is derived from the selected component to avoid a redundant
// kind switch in the UI (component keys are disjoint between the two kinds).
const DYNAMIC_DEFAULT_KEYS = [
  { key: 'task_completion', weight: 0.45 },
  { key: 'response_efficiency', weight: 0.3 },
  { key: 'tool_health', weight: 0.25 },
] as const

function scoreComponentOptions(status: ScoreStatusResponse | undefined) {
  if (!status) return [] as ReturnType<typeof scoreDimensionsForRun>
  const fromKind = (kind: 'static' | 'dynamic') => {
    const run = latestRun(status, kind)
    return run ? scoreDimensionsForRun(run) : []
  }
  const dynamicOptions = fromKind('dynamic')
  if (dynamicOptions.length === 0 && status.dynamic_status && status.dynamic_status !== 'failed') {
    return [
      ...fromKind('static'),
      ...DYNAMIC_DEFAULT_KEYS.map(({ key, weight }) => ({
        kind: 'dynamic' as const,
        key,
        label: dimensionLabel(key),
        score: status.dynamic_score ?? 0,
        weight,
        contribution: (status.dynamic_score ?? 0) * weight,
      })),
    ]
  }
  return [...fromKind('static'), ...dynamicOptions]
}

function scoreKindLabel(kind: string) {
  if (kind === 'static') return '静态评分'
  if (kind === 'dynamic') return '动态评分'
  if (kind === 'manual_adjustment') return '人工调分'
  if (kind === 'final') return '综合分'
  return kind
}

function formatScore(value: number | null | undefined) {
  return value == null ? '-' : value.toFixed(1)
}

function scoreTone(status: string): 'blue' | 'green' | 'amber' | 'red' | 'neutral' {
  if (status === 'scored') return 'green'
  if (status.includes('failed')) return 'red'
  if (status.includes('pending') || status.includes('running')) return 'amber'
  return 'blue'
}

function Metric({ icon, label, value }: { icon: ReactNode; label: string; value: number }) { return <Card className="metric-card"><span>{icon}</span><div><strong>{value}</strong><small>{label}</small></div></Card> }
